<script setup>
// One day of one area group (X47; X54 the flat table): a column per playing
// area under the club's own name (and X53's short description, an "Open
// play" chip on a walk-up area), time rows on the group's one slot step with
// hairlines on both axes (stronger on the hour), and blocks drawn by
// BookingCell: a booking is ONE block from its first slot to its last, a run
// of Open play / Can't book / Past is one band, a free cell is a cell of its
// own showing its price (rulings 6, 7, 8). Columns keep a fixed comfortable
// width; with many the card scrolls sideways with the time column pinned
// left. X59 (Grant 2026-10-19): the card never scrolls up and down: the grid
// grows to its full height and the PAGE scrolls, while the column headers
// live in their own row above the scroller, sticky to the page top under
// the site's bar, kept over their columns by usePinnedGridHead. The rows of
// today that have already started fold into one "Earlier today" band so the
// first bookable row sits under the header (D2 item 5). Press a free cell and drag down or up to
// book several slots in one go (ruling 5): mouse and pen at once, touch after
// a hold (a swipe still scrolls), autoscroll near the edges (sideways the
// card, up and down the page), Escape drops it;
// Shift+Down/Up stretches from a focused cell and Enter books; a plain tap or
// click is one slot. The ghost shows the range, its length and price, or in
// red why it can't be booked. One shared tooltip follows the pointer; on
// touch a tap on a cell that can't be booked toasts its reason.
// X232 (R96 M1): in the scheduling table (noDrag) a mouse click or tap on a cell that can't be picked
// (Too short, Taken, Frozen…) explains itself too: the line under the table says why.
//
// X91 (Grant 2026-10-21): the time column is the shared BookingTimeColumn: an ordinary sticky cell per row with the
// row's whole period in 24-hour time ("8:00–8:30"), no labels sitting on the lines; the top-left corner cell holds
// whatever the page puts in the `corner` slot (the legend's button on /book).
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import BookingCell from '~/components/bookings/BookingCell.vue';
import BookingTimeColumn from '~/components/bookings/BookingTimeColumn.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import {createKeyIntent, isClick} from '~/utils/bookingDrag';
import {usePinnedGridHead} from '~/composables/usePinnedGridHead';
import {useScrollCues} from '~/composables/useScrollCues';
import {dragRange, pressRow, rangeLabel, rangeValidity, rowHeight, rowPeriods, tapTarget} from '~/utils/bookingWeek';
import {pickOverlay} from '~/utils/scheduleBooking';

const props = defineProps({
  /** From dayGrid(): {rows, step, columns: [{area, cells, blocks}], empty, startedRows}. */
  grid: {type: Object, default: null},
  /** {areaId, startsAt, slots} of the range being booked right now. */
  selected: {type: Object, default: null},
  loading: {type: Boolean, default: false},
  /** The viewer's allowance on this group: {maxDurationMinutes, allDay} caps a dragged range. */
  allowance: {type: Object, default: null},
  /** The viewer's per-slot price on this group (null = free), for the ghost. */
  priceMinor: {type: Number, default: null},
  currency: {type: String, default: 'gbp'},
  /** The earlier rows of today are shown (the band is open). */
  pastOpen: {type: Boolean, default: false},
  touch: {type: Boolean, default: false},
  /** X214: a pick is one start (the schedule dialog: the match's own length spans the rows), never a dragged range. */
  noDrag: {type: Boolean, default: false},
  /** Grant 2026-11-02: the club's word for the columns, plural ("courts"), for the sideways arrows' tooltips. */
  nounPlural: {type: String, default: null},
});
const emit = defineEmits(['tap', 'explain', 'update:pastOpen']);

// The row height: from the slot step, never under 40px on a phone (the CSS says the same; the drag maths read it from the DOM).
const rh = computed(() => rowHeight(props.grid?.step));
const rowPx = ref(0);
const measure = () => { const el = scrollEl.value?.querySelector('.bw-row'); rowPx.value = el ? el.getBoundingClientRect().height : 0; };
const rhPx = () => rowPx.value || rh.value;
const rows = computed(() => props.grid?.rows ?? []);
// The band folds the started rows away only while some rows are still to come (a past day shows as it is).
const foldable = computed(() => (props.grid?.startedRows ?? 0) > 0 && (props.grid?.startedRows ?? 0) < rows.value.length);
const hidden = computed(() => (foldable.value && !props.pastOpen ? props.grid.startedRows : 0));
const visibleRows = computed(() => rows.value.slice(hidden.value));
// X91: the time column's cells, each the row's whole period (from the full rows, so the first shown row ends right).
const timeRows = computed(() => rowPeriods(rows.value, props.grid?.step).slice(hidden.value).map((r) => ({key: r.minutes, period: r.period, hour: r.hour})));
// Grant 2026-11-02 ("While the table is loading it flashes the open-play words instead of a table skeleton loader, the
// screen jumps and it looks terrible"): a day whose data hasn't arrived (no grid at all, dayGrid's `pending`, or a load
// in flight with no table to dim) is drawn as a table-shaped skeleton (the header row, the time column, the cell grid)
// on the same CSS grid as the real table, so nothing moves when the day lands. Its shape is the last real table's
// (columns, rows shown, step), so a week or sport change keeps the size on screen; before any table a plain default.
const SKELETON_SHAPE = {cols: 3, rows: 10, step: 60};
const shape = ref(SKELETON_SHAPE);
const hasTable = computed(() => !!props.grid && !props.grid.pending && !props.grid.empty && !props.grid.closedAllDay);
const pending = computed(() => !props.grid || !!props.grid.pending || (props.loading && !hasTable.value && !props.grid.closedAllDay));
watch(hasTable, (has) => { if (has) shape.value = {cols: props.grid.columns.length, rows: Math.max(1, visibleRows.value.length), step: props.grid.step}; }, {immediate: true});
const skelRows = computed(() => Array.from({length: shape.value.rows}, (_, i) => ({key: i, hour: (i * (shape.value.step || 60)) % 60 === 0})));
const style = computed(() => (pending.value
  ? {'--bw-rh': rowHeight(shape.value.step) + 'px', '--bw-cols': shape.value.cols, '--bw-rows': shape.value.rows}
  : {'--bw-rh': rh.value + 'px', '--bw-cols': props.grid?.columns?.length ?? 0, '--bw-rows': visibleRows.value.length, '--bw-vw': viewW.value ? viewW.value + 'px' : null}));
const earlierLabel = computed(() => {
  if (!foldable.value) return '';
  const from = rows.value[0]?.label ?? '';
  const to = rows.value[props.grid.startedRows]?.label ?? '';
  return `Earlier today · ${from}–${to}`;
});

// A block clipped to the visible rows (a booking in progress keeps its visible tail).
const visibleBlocks = (col) => (col.blocks ?? []).flatMap((b) => {
  const end = b.row + b.span;
  if (end <= hidden.value) return [];
  const row = Math.max(b.row, hidden.value) - hidden.value;
  return [{...b, vRow: row, vSpan: end - hidden.value - row}];
});
const blockStyle = (blk) => ({top: `calc(${blk.vRow} * var(--bw-rh-eff))`, height: `calc(${blk.vSpan} * var(--bw-rh-eff))`});
const isTall = (blk) => blk.vSpan * rhPx() >= 44 && !!blk.sub;
const isBooking = (blk) => blk.state === 'taken' || blk.state === 'mine';
// X243 (TEST_FT-R3 M-B): in the scheduling table a tall "This match" block (the match's own booking, or its session's on
// another court) and the pick outline keep their words in their FIRST row, never centred down the block: a click on
// the words keeps the start (tapTarget), a click lower down picks that later row.
const wordsTop = (blk) => props.noDrag && blk.state === 'mine' && blk.vSpan > 1;

// The picked range (while the sheet is open) is drawn selected.
const selectedRows = (col) => {
  const s = props.selected;
  if (!s || s.areaId !== col.area.id) return null;
  const start = col.cells.findIndex((c) => c.startsAt === s.startsAt);
  if (start < 0) return null;
  return {from: start, to: start + Math.max(1, s.slots ?? 1)};
};
// X219 (R89 M-1): a block is selected when it overlaps the picked rows (a booking block can start on the picked row or before it).
const isSelected = (col, blk) => { const r = selectedRows(col); return !!r && blk.row < r.to && blk.row + (blk.span ?? 1) > r.from; };
// X233 (R97 L-2): in the scheduling table (noDrag) the picked match is ONE block over its rows, its times on it, so a
// row inside it that is Too short on its own never reads "Too short" in white on red; the cells under it are not
// drawn selected. /book and the admin calendar keep their per-cell selection.
const pickFor = (col) => (props.noDrag ? pickOverlay(col, props.selected, hidden.value) : null);
const pickStyle = (p) => ({top: `calc(${p.row} * var(--bw-rh-eff) + 1px)`, height: `calc(${p.span} * var(--bw-rh-eff) - 1px)`});
// X241 (TEST_FT-R2 L1): a past-closing pick's "Closes 22:00" fits a 30-minute row (34px), so it shows from 32px up.
const pickTall = (p) => !!p.sub && p.span * rhPx() >= (p.closing ? 32 : 44);

// One tooltip for every block: the hovered (or focused) block is its activator.
const hover = ref(null);
const hoverTip = ref('');
const enter = (e, blk) => { if (drag.value?.active) return; hover.value = e.currentTarget; hoverTip.value = blk.tip || ''; };
const leave = () => { hover.value = null; };

const tap = (col, blk, slots = 1, originRow = null) => {
  // X240 (TEST_R100 M-1): in the scheduling table the match's own booking is ONE block over its rows; a click on a later
  // row inside it picks that row's start when the match fits from there (sliding the match, or its whole session, later
  // on the same court), not the block's first row again.
  // X242 (TEST_R101 M-1): tapTarget reads the row from the press (pressRow no longer caps it at the first row).
  const t = tapTarget(col, blk, originRow, {noDrag: props.noDrag, hidden: hidden.value});
  if (t.tap) emit('tap', {cell: t.tap, column: col, slots});
  // X233 (R97 L-3): the block's tooltip (a booking's whole span, "12:00–13:00"), not its first slot's.
  else emit('explain', {cell: blk.cell, column: col, tip: blk.tip});
};
const label = (col, blk) => `${col.area.name}, ${blk.range || ''}: ${blk.tip || blk.line || blk.state}`;

// ── Drag to book a range (ruling 5; the pointer handling follows the admin calendar's X40) ──
const scrollEl = ref(null);
const headEl = ref(null);
const headClip = ref(null);
const {sync: syncHead, autoscroll} = usePinnedGridHead(scrollEl, headClip, headEl);

// Grant 2026-11-02 ("when there are more courts than fit and the grid scrolls horizontally, it isn't obvious there are
// more columns off-screen"): a soft white fade on the edge that has more (the right; the left once scrolled, starting
// after the pinned time column) and a small round arrow button at that end of the header row that scrolls one column;
// both gone when every column fits (useScrollCues, shared with the week strip's days). The scroller's visible width
// (--bw-vw) also sizes the "Earlier today" band, so it stays put across what is seen instead of scrolling away.
const {cues, viewW, update: updateCues} = useScrollCues(scrollEl);
const colWidth = () => scrollEl.value?.querySelector('.bw-col')?.getBoundingClientRect().width || 176;
const scrollCols = (dir) => { const s = scrollEl.value; if (!s) return; try { s.scrollBy({left: dir * colWidth(), behavior: 'smooth'}); } catch { s.scrollLeft += dir * colWidth(); } };
const plural = computed(() => props.nounPlural || 'playing areas');
const moreTip = computed(() => `More ${plural.value}`);
const earlierTip = computed(() => `Earlier ${plural.value}`);
const drag = ref(null); // {pointerId, pointerType, startX, startY, ci, originRow, colEl, active, moved, target}
const kb = ref(null);   // a keyboard range: {ci, anchorRow, cursorRow, target}
const LONG_PRESS_MS = 300;
const TOUCH_SLOP = 8;
let raf = 0;
let pressTimer = 0;
let last = {x: 0, y: 0};

const resolveRange = (ci, originRow, pointerRow) => {
  const col = props.grid.columns[ci];
  const {row, span} = dragRange(originRow, pointerRow, rows.value.length);
  const valid = rangeValidity(col.cells, row, span, {maxDurationMinutes: props.allowance?.maxDurationMinutes ?? null, allDay: !!props.allowance?.allDay, slotMinutes: props.grid.step});
  const opts = {slotMinutes: props.grid.step, priceMinor: props.priceMinor, currency: props.currency};
  // X58 (R23a L2): the ghost names the area ("Court 2 · 6pm–7:30pm · 1 h 30 · £12.00"); the grid draws the name on its own line.
  return {row, span, valid, name: col.area.name, details: rangeLabel(rows.value, row, span, opts), label: rangeLabel(rows.value, row, span, {...opts, areaName: col.area.name})};
};
const onDown = (e, ci, blk) => {
  if (e.button != null && e.button !== 0) return;
  if (!blk.bookable) return;
  kb.value = null;
  const touch = e.pointerType === 'touch';
  const rect = e.currentTarget.getBoundingClientRect();
  measure();
  const originRow = pressRow(blk, e.clientY - rect.top, rhPx(), {noDrag: props.noDrag, hidden: hidden.value});
  drag.value = {pointerId: e.pointerId, pointerType: e.pointerType, startX: e.clientX, startY: e.clientY, ci, originRow, colEl: e.currentTarget.parentElement, active: !touch, moved: false, target: null};
  last = {x: e.clientX, y: e.clientY};
  hover.value = null;
  if (touch) {
    clearTimeout(pressTimer);
    pressTimer = setTimeout(() => { if (drag.value && !drag.value.moved && !props.noDrag) { drag.value.active = true; drag.value.moved = true; schedule(); } }, LONG_PRESS_MS);
  }
  try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not needed */ }
  window.addEventListener('keydown', onDragKey);
};
const update = () => {
  raf = 0;
  const d = drag.value;
  if (!d || !d.active) return;
  const top = d.colEl?.getBoundingClientRect().top ?? 0;
  const pointerRow = hidden.value + (last.y - top) / rhPx();
  d.target = resolveRange(d.ci, d.originRow, pointerRow);
  // Near an edge: the card scrolls sideways, the page up and down; then re-read the pointer against the moved column.
  if (autoscroll(last.x, last.y)) schedule();
};
const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
const onMove = (e) => {
  const d = drag.value;
  if (!d || e.pointerId !== d.pointerId) return;
  last = {x: e.clientX, y: e.clientY};
  if (!d.active) {
    if (!isClick(e.clientX - d.startX, e.clientY - d.startY, TOUCH_SLOP)) endDrag();
    return;
  }
  if (!d.moved && isClick(e.clientX - d.startX, e.clientY - d.startY)) return;
  if (props.noDrag) return;
  d.moved = true;
  schedule();
};
const onTouchMove = (e) => { if (drag.value?.active && drag.value.pointerType === 'touch') e.preventDefault(); };
const endDrag = () => {
  clearTimeout(pressTimer);
  if (raf) { cancelAnimationFrame(raf); raf = 0; }
  drag.value = null;
  window.removeEventListener('keydown', onDragKey);
};
const onDragKey = (e) => { if (e.key === 'Escape' && drag.value) { e.preventDefault(); endDrag(); } };
const onUp = (e, col, blk) => {
  const d = drag.value;
  if (!d || e.pointerId !== d.pointerId) return;
  const wasClick = !d.moved;
  const target = d.target;
  const originRow = d.originRow;
  endDrag();
  if (wasClick) { tap(col, blk, 1, originRow); return; }
  if (!target) return;
  if (!target.valid.ok) { emit('explain', {cell: blk.cell, column: col, reason: target.valid.reason}); return; }
  emit('tap', {cell: col.cells[target.row], column: col, slots: target.span});
};
const onCancel = () => endDrag();
// Pointer taps are handled on pointerup; a keyboard activation (Enter/Space, detail 0) books one slot.
const onClick = (e, col, blk) => { if (e.detail === 0 && !kb.value) tap(col, blk); };

// Keyboard: Shift+Down/Up stretch a range from the focused cell, Enter books it, Escape drops it.
const onKey = (e, col, ci, blk) => {
  if (!blk.bookable) return;
  const intent = createKeyIntent(e);
  if (!intent) return;
  if (props.noDrag && intent.kind !== 'confirm') return;
  const k = kb.value;
  const own = k && k.ci === ci && k.anchorRow === blk.row;
  if (intent.kind === 'cancel') { if (k) { e.preventDefault(); kb.value = null; } return; }
  if (intent.kind === 'confirm') {
    e.preventDefault();
    if (own && k.target) { const t = k.target; kb.value = null; if (t.valid.ok) emit('tap', {cell: col.cells[t.row], column: col, slots: t.span}); else emit('explain', {cell: blk.cell, column: col, reason: t.valid.reason}); }
    else tap(col, blk);
    return;
  }
  e.preventDefault();
  const cursor = Math.max(0, Math.min(rows.value.length - 1, (own ? k.cursorRow : blk.row) + intent.dir));
  kb.value = {ci, anchorRow: blk.row, cursorRow: cursor, target: resolveRange(ci, blk.row, cursor)};
};
// Another day, tab or set of columns drops a range in hand (the minute tick redraws the same grid and keeps it).
watch(() => `${props.grid?.columns?.map((c) => c.area.id).join()}|${props.grid?.rows?.length}|${props.grid?.rows?.[0]?.minutes}|${props.grid?.columns?.[0]?.cells?.[0]?.date}`, () => { kb.value = null; endDrag(); });

// The ghost: the live range while dragging, or the keyboard range.
const ghostFor = (ci) => {
  const d = drag.value;
  if (d?.active && d.moved && d.ci === ci && d.target) return {...d.target, hint: d.target.valid.ok ? '' : d.target.valid.reason};
  const k = kb.value;
  if (k && k.ci === ci && k.target) return {...k.target, hint: k.target.valid.ok ? 'Enter to book · Esc to cancel' : k.target.valid.reason};
  return null;
};
const ghostStyle = (g) => {
  const row = Math.max(g.row, hidden.value) - hidden.value;
  return {top: `calc(${row} * var(--bw-rh-eff))`, minHeight: `calc(${Math.max(0, g.row + g.span - hidden.value - row)} * var(--bw-rh-eff))`};
};
const creating = computed(() => !!(drag.value?.active && drag.value.moved));

onMounted(() => { scrollEl.value?.addEventListener('touchmove', onTouchMove, {passive: false}); measure(); });
watch(() => [props.grid, props.pastOpen], () => { requestAnimationFrame(() => { measure(); syncHead(); updateCues(); }); });
onBeforeUnmount(() => { scrollEl.value?.removeEventListener('touchmove', onTouchMove); endDrag(); });
</script>

<template>
  <div class="bw" :class="{'bw--loading': loading && !pending, 'bw--creating': creating}" :style="style" :aria-busy="pending || loading || undefined">
    <!-- Grant 2026-11-02: the day's data isn't here yet: the table's skeleton, never one of the day's own words -->
    <div v-if="pending" class="bw-skel" role="status" aria-label="Loading the day">
      <div class="bw-head">
        <div class="bw-corner"><slot name="corner"/></div>
        <div class="bw-head-clip">
          <div class="bw-head-track">
            <div v-for="c in shape.cols" :key="'sh' + c" class="bw-area" aria-hidden="true">
              <span class="bw-skel-bar bw-skel-bar--name"/>
              <span class="bw-skel-bar bw-skel-bar--desc"/>
            </div>
          </div>
        </div>
      </div>
      <div class="bw-scroll">
        <div class="bw-table" aria-hidden="true">
          <div class="bw-times bw-skel-times">
            <div v-for="r in skelRows" :key="'st' + r.key" class="bw-skel-time" :class="{'bw-skel-time--hour': r.hour}"><span class="bw-skel-bar bw-skel-bar--time"/></div>
          </div>
          <div v-for="c in shape.cols" :key="'sc' + c" class="bw-col">
            <div v-for="r in skelRows" :key="r.key" class="bw-row bw-skel-row" :class="{'bw-row--hour': r.hour}"><span class="bw-skel-bar bw-skel-bar--cell"/></div>
          </div>
        </div>
      </div>
    </div>
    <!-- X94 (R35 M2): a day closed from start to end is one compact state, not 48 closed rows from 0:00 to 24:00 -->
    <div v-else-if="grid?.closedAllDay" class="bw-empty bw-empty--closed">
      <v-icon size="28" color="charcoal" icon="mdi-cancel"/>
      <div class="bw-empty-title">Closed all day</div>
      <div class="bw-empty-sub">Nobody can play or book on this day.</div>
    </div>
    <div v-else-if="grid.empty" class="bw-empty">
      <v-icon size="28" color="charcoal" icon="mdi-weather-sunny"/>
      <div class="bw-empty-title">Open play all day</div>
      <div class="bw-empty-sub">Nothing to book today: just turn up.</div>
    </div>
    <template v-else>
      <!-- X59: the column headers, pinned to the page top (under the site's bar) and kept over their columns -->
      <div ref="headEl" class="bw-head">
        <div class="bw-corner"><slot name="corner"/></div>
        <div ref="headClip" class="bw-head-clip">
          <div class="bw-head-track">
            <div v-for="col in grid.columns" :key="'h' + col.area.id" class="bw-area">
              <span class="bw-area-name">{{ col.area.name }}</span>
              <v-tooltip v-if="col.area.description" :text="col.area.description" location="top" :disabled="touch">
                <template #activator="{props: tip}"><span v-bind="tip" class="bw-area-desc">{{ col.area.description }}</span></template>
              </v-tooltip>
              <span v-if="!col.area.canBeBooked" class="bw-area-chip"><v-icon size="12" icon="mdi-walk"/>Open play</span>
            </div>
          </div>
        </div>
        <!-- Grant 2026-11-02: an arrow at the end that has more columns; each scrolls one column -->
        <div v-if="cues.left" class="bw-cue-btn bw-cue-btn--l"><tl-icon-btn :tooltip="earlierTip" icon="mdi-chevron-left" :icon-size="18" @click="scrollCols(-1)"/></div>
        <div v-if="cues.right" class="bw-cue-btn bw-cue-btn--r"><tl-icon-btn :tooltip="moreTip" icon="mdi-chevron-right" :icon-size="18" @click="scrollCols(1)"/></div>
      </div>
      <div class="bw-body">
      <div ref="scrollEl" class="bw-scroll">
        <div class="bw-table">
          <!-- The earlier rows of today, folded (D2 item 5) -->
          <button v-if="foldable" type="button" class="bw-earlier" :aria-expanded="pastOpen" @click="emit('update:pastOpen', !pastOpen)">
            <v-icon size="16" :icon="pastOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"/>
            <span>{{ pastOpen ? 'Hide earlier' : earlierLabel }}</span>
            <span v-if="!pastOpen" class="bw-earlier-n">{{ grid.startedRows }} {{ grid.startedRows === 1 ? 'slot' : 'slots' }}</span>
          </button>
  
          <booking-time-column :rows="timeRows" class="bw-times"/>
          <div v-for="(col, ci) in grid.columns" :key="'c' + col.area.id" class="bw-col">
            <div v-for="r in visibleRows" :key="r.minutes" class="bw-row" :class="{'bw-row--hour': r.hour}"/>
            <button
                v-for="blk in visibleBlocks(col)" :key="col.area.id + ':' + blk.row" type="button"
                class="bw-block" :class="{'bw-block--tap': blk.bookable, 'bw-block--booking': isBooking(blk), 'bw-block--band': !blk.bookable && !isBooking(blk)}"
                :style="blockStyle(blk)" :aria-label="label(col, blk)"
                @pointerdown="onDown($event, ci, blk)" @pointermove="onMove" @pointerup="blk.bookable ? onUp($event, col, blk) : null" @pointercancel="onCancel"
                @click="blk.bookable ? onClick($event, col, blk) : (($event.detail === 0 || touch || noDrag) && tap(col, blk))"
                @keydown="onKey($event, col, ci, blk)"
                @mouseenter="enter($event, blk)" @mouseleave="leave" @focus="enter($event, blk)" @blur="leave">
              <booking-cell :model="blk" :selected="!noDrag && isSelected(col, blk)" :tall="isTall(blk)" :top="wordsTop(blk)"/>
            </button>
            <div v-if="pickFor(col)" class="bw-pick" :class="{'bw-pick--tall': pickTall(pickFor(col)), 'bw-pick--top': pickFor(col).span > 1}" :style="pickStyle(pickFor(col))" aria-hidden="true">
              <span class="bw-pick-in">
                <span class="bw-pick-t">{{ pickFor(col).text }}</span>
                <span v-if="pickTall(pickFor(col))" class="bw-pick-s">{{ pickFor(col).sub }}</span>
              </span>
            </div>
            <div v-if="ghostFor(ci)" class="bw-ghost" :class="ghostFor(ci).valid.ok ? 'bw-ghost--ok' : 'bw-ghost--no'" :style="ghostStyle(ghostFor(ci))" :aria-label="ghostFor(ci).valid.ok ? ghostFor(ci).label : ghostFor(ci).hint" aria-live="polite">
              <span v-if="ghostFor(ci).valid.ok" class="bw-ghost-n">{{ ghostFor(ci).name }}</span>
              <span class="bw-ghost-t">{{ ghostFor(ci).valid.ok ? ghostFor(ci).details : ghostFor(ci).hint }}</span>
              <span v-if="ghostFor(ci).valid.ok && ghostFor(ci).hint" class="bw-ghost-h">{{ ghostFor(ci).hint }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Grant 2026-11-02: the soft fade on an edge with more to scroll (the left one starts after the pinned time column) -->
      <div v-if="cues.left" class="bw-fade bw-fade--l" aria-hidden="true"/>
      <div v-if="cues.right" class="bw-fade bw-fade--r" aria-hidden="true"/>
      </div>
      <v-tooltip v-if="hover && !touch" :activator="hover" :model-value="true" :text="hoverTip" location="top" :open-delay="0"/>
    </template>
  </div>
</template>

<style scoped lang="scss">
@use './booking-table' as *;

.bw {
  --bw-col: 176px;
  --bw-line: rgb(var(--v-theme-borderLight));
  --bw-line-hour: #D4D4D4;
  --bw-rh-eff: var(--bw-rh);
  /* X91: the shared time column's row height. */
  --bt-rh: var(--bw-rh-eff);
  position: relative;
  transition: opacity .15s ease;
  &--loading { opacity: .55; }
  &--creating { cursor: ns-resize; user-select: none; }
  /* X91: the shared time column's width (--bw-tw), the admin calendar's too. */
  @include booking-time-width('--bw-tw');
}
/* X59: the page scrolls, not the card: the grid is its full height and the card scrolls sideways only, when the
   columns don't fit, with the time column pinned left. The headers sit in their own row above, sticky to the page top
   under the site's bar (var(--v-layout-top); 0 in the branded frame), their clip's scrollLeft following the card's.
   The card (.bpb-card) clips with overflow: clip, not hidden, so the sticky row pins to the viewport. */
.bw-head {
  position: sticky; top: var(--v-layout-top, 0px); z-index: 3;
  display: flex; align-items: stretch;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid var(--bw-line-hour);
}
.bw-corner { flex: 0 0 var(--bw-tw); width: var(--bw-tw); display: flex; align-items: center; justify-content: center; border-right: 1px solid var(--bw-line); }
.bw-head-clip { flex: 1 1 auto; min-width: 0; overflow: hidden; }
.bw-head-track {
  display: grid;
  grid-template-columns: repeat(var(--bw-cols), var(--bw-col));
  width: max-content;
  min-width: 100%;
}
.bw-body { position: relative; }
.bw-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
}
/* Grant 2026-11-02: more columns off-screen that way: a soft white fade on that edge of the scroller (never over the
   pinned time column: the left one starts after it) and a small round arrow at that end of the header row. Both only
   while there is more to scroll that way (scrollCues); nothing when every column fits. */
.bw-fade {
  position: absolute; top: 0; bottom: 0; width: 36px; z-index: 2; pointer-events: none;
  &--r { right: 0; background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, .92)); }
  &--l { left: var(--bw-tw); background: linear-gradient(to left, rgba(255, 255, 255, 0), rgba(255, 255, 255, .92)); }
}
.bw-cue-btn {
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 4;
  &--r { right: 6px; }
  &--l { left: calc(var(--bw-tw) + 6px); }
  :deep(.tl-iconbtn) { width: 28px; height: 28px; border-radius: 999px; box-shadow: 0 1px 4px rgba(0, 0, 0, .12), 0 0 0 3px rgb(var(--v-theme-surface)); }
}
.bw-table {
  display: grid;
  grid-template-columns: var(--bw-tw) repeat(var(--bw-cols), var(--bw-col));
  grid-template-rows: auto calc(var(--bw-rows) * var(--bw-rh-eff));
  width: max-content;
  min-width: 100%;
}
.bw-area {
  display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start; gap: 2px;
  min-height: 44px; padding: 8px 10px 7px;
  background: rgb(var(--v-theme-surface));
  @include booking-table-column(var(--bw-line));
}
.bw-area-name { font-size: 13px; font-weight: 700; color: rgb(var(--v-theme-text)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
.bw-area-desc { font-size: 11px; color: rgb(var(--v-theme-textSubtle)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; line-height: 1.2; }
.bw-area-chip {
  display: inline-flex; align-items: center; gap: 3px; margin-top: 2px; padding: 1px 7px 1px 5px; border-radius: 999px;
  font-size: 10.5px; font-weight: 600; color: rgb(var(--v-theme-textSubtle)); background: rgb(var(--v-theme-light));
}
/* Grant 2026-11-02: the band is as wide as what is seen of the scroller (--bw-vw, measured; the whole table before
   that) and sticky left, so its words and toggle stay put whatever the sideways scroll. */
.bw-earlier {
  grid-column: 1 / -1; grid-row: 1; justify-self: start; width: var(--bw-vw, 100%); box-sizing: border-box;
  display: flex; align-items: center; gap: 6px; height: 34px; padding: 0 10px 0 8px;
  background: #F4F4F4; color: rgb(var(--v-theme-charcoal)); border: 0; border-bottom: 1px solid var(--bw-line-hour);
  font: inherit; font-size: 11.5px; font-weight: 600; cursor: pointer; text-align: left;
  position: sticky; left: 0;
  &:hover { background: #EEEEEE; }
  &:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: -2px; }
}
.bw-earlier-n { margin-left: auto; font-size: 11px; font-weight: 600; color: rgb(var(--v-theme-textSubtle)); }

/* X91: the shared BookingTimeColumn (sticky left, a cell per row); it sits in the table's second grid row. */
.bw-times { grid-row: 2; }

/* The table: a hairline between columns and one per slot, stronger on the hour; blocks sit on top, flush. */
.bw-col { grid-row: 2; position: relative; @include booking-table-column(var(--bw-line)); }
.bw-row { height: var(--bw-rh-eff); box-sizing: border-box; border-top: 1px solid var(--bw-line); &--hour { border-top-color: var(--bw-line-hour); } }
.bw-row:first-child { border-top-color: transparent; }
.bw-block {
  position: absolute; left: 0; right: 0; box-sizing: border-box;
  display: block; padding: 1px 0 0; margin: 0; border: none; background: none; font: inherit; color: inherit; text-align: center;
  cursor: default; overflow: hidden;
  touch-action: pan-x pan-y;
  &--tap { cursor: pointer; }
  /* A booking block: inset a touch with a small radius of its own (ruling 7), the hairlines showing through at its edges. */
  &--booking { left: 2px; right: 2px; padding: 2px 0 1px; > :deep(.bwc) { border-radius: 4px; } }
  &:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: -2px; z-index: 1; }
}
.bw-block--band { pointer-events: auto; }

/* X233 (R97 L-2): the scheduling table's picked match, one brand block over all its rows (a booking's inset and radius),
   its times on it. X242 (TEST_R101 M-1): it lets clicks through (pointer-events: none) to the block under it, so the
   first click on a later row of the match's own block, straight after the dialog opens, picks that row. */
.bw-pick {
  position: absolute; left: 2px; right: 2px; box-sizing: border-box; z-index: 2; pointer-events: none;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
  padding: 2px 6px;
  border-radius: 4px; background: rgb(var(--v-theme-primary)); color: #fff;
  font-size: 11.5px; font-weight: 700; line-height: 1.15; text-align: center; overflow: hidden;
}
/* X243 (TEST_FT-R3 M-B): the words of a pick taller than one row sit in its first row (where a click keeps the start). */
.bw-pick-in { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; max-width: 100%; min-width: 0; }
.bw-pick--top { justify-content: flex-start; padding-top: 0; padding-bottom: 0; }
.bw-pick--top > .bw-pick-in { height: calc(var(--bw-rh-eff) - 1px); flex: 0 0 auto; }
.bw-pick-t, .bw-pick-s { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
.bw-pick-s { font-size: 10.5px; font-weight: 600; opacity: .9; }

/* The live range while dragging, or a keyboard range. X58 (R23a L1, L2): the area's name on its own line, the words wrap
   rather than cut ("Longer than your longest booking (2 h)" reads whole in a 200px column), and a one-row ghost grows to
   fit its two lines (its height is a minimum, so nothing is ever clipped). */
.bw-ghost {
  position: absolute; left: 1px; right: 1px; box-sizing: border-box; z-index: 2; pointer-events: none;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; padding: 2px 6px;
  border-radius: 4px; font-size: 11.5px; font-weight: 700; line-height: 1.25; text-align: center;
  &--ok { border: 2px solid rgb(var(--v-theme-primary)); background: linear-gradient(rgba(var(--v-theme-primary), .1), rgba(var(--v-theme-primary), .1)), rgb(var(--v-theme-surface)); color: rgb(var(--v-theme-primary)); }
  &--no { border: 2px dashed rgb(var(--v-theme-error)); background: linear-gradient(rgba(var(--v-theme-error), .08), rgba(var(--v-theme-error), .08)), rgb(var(--v-theme-surface)); color: rgb(var(--v-theme-error)); }
}
.bw-ghost-n { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; font-size: 11px; font-weight: 600; opacity: .9; }
.bw-ghost-t { white-space: normal; overflow-wrap: anywhere; max-width: 100%; }
.bw-ghost-h { font-size: 10.5px; font-weight: 600; opacity: .9; }

/* Grant 2026-11-02: the loading skeleton: the real table's header row, time column and cells (the same grid, widths and
   row height, so the day lands in place), light-grey bars (the kit's skeleton grey, settings.scss) under one shimmer. */
.bw-skel { position: relative; overflow: hidden; }
.bw-skel::after {
  content: ''; position: absolute; inset: 0; z-index: 3; pointer-events: none;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0, rgba(255, 255, 255, .65) 50%, rgba(255, 255, 255, 0) 100%);
  transform: translateX(-100%);
  animation: bw-shimmer 1.5s infinite;
}
@keyframes bw-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .bw-skel::after { animation: none; } }
.bw-skel-bar { display: block; height: 10px; border-radius: 5px; background: #ECECEC; }
.bw-skel-bar--name { width: 64%; height: 12px; margin-top: 2px; }
.bw-skel-bar--desc { width: 40%; height: 8px; margin-top: 4px; }
.bw-skel-times { position: sticky; left: 0; z-index: 2; background: rgb(var(--v-theme-surface)); border-right: 1px solid var(--bw-line); }
.bw-skel-time { height: var(--bw-rh-eff); box-sizing: border-box; display: flex; align-items: center; justify-content: center; border-top: 1px solid var(--bw-line); &--hour { border-top-color: var(--bw-line-hour); } }
.bw-skel-time:first-child { border-top-color: transparent; }
.bw-skel-bar--time { width: 56px; max-width: calc(100% - 8px); height: 9px; }
.bw-skel-row { display: flex; align-items: center; justify-content: center; }
.bw-skel-bar--cell { width: 38%; height: 9px; opacity: .8; }

.bw-empty { text-align: center; padding: 28px 16px; }
.bw-empty-title { font-size: 14px; font-weight: 700; margin-top: 6px; }
.bw-empty-sub { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); margin-top: 2px; }

@media (min-width: 1024px) {
  .bw { --bw-col: 200px; }
}
@media (max-width: 640px) {
  .bw { --bw-col: 132px; --bw-rh-eff: max(var(--bw-rh), 40px); }
  .bw-area { padding: 7px 8px 6px; min-height: 40px; }
  .bw-area-name { font-size: 12px; }
  /* X244 (TEST_R102 L-5): a phone's 132px column fits the pick's longest times ("21:00–0:30 next day", 116px at
     11.5px, cut by 1px): a narrower inset and the cells' 11px type give it room (about 110px of 120px). */
  .bw-pick { padding-left: 4px; padding-right: 4px; font-size: 11px; }
}
</style>
